import useValidationForRequiredField from '@/form/useValidationForRequiredField';
import React from 'react';
import { Field, getIn } from 'formik';
import FormElement from '@components/presentational/Form/FormElement';
import CKEditor from 'ckeditor4-react';
import { Box, useTheme } from '@chakra-ui/react';

interface RichTextEditorProps {
  label?: string;
  name: string;
  validate?: (value: string) => string | undefined;
  required?: boolean;
  requiredMessage?: string;
}

export default function RichTextEditor({
  label,
  name,
  validate,
  required,
  requiredMessage,
}: RichTextEditorProps) {
  const validateForm = useValidationForRequiredField(
    required,
    validate,
    requiredMessage
  );

  const customCSS = `
  * {
    font-family: Marianne,Arial,sans-serif;
  }
  p, ul, ol {
    line-height: 1.8;
  }
  h2 {
    font-size: 24px;
    font-weight: normal;
  }
  h3 {
    font-size: 20px;
    font-weight: normal;
  }
  a {
    text-decoration: underline;
    color: inherit;
    cursor: pointer;
  }
  a:hover {
    color: #3558A2;
  }
  `;
  const theme = useTheme();
  return (
    <Field name={name} validate={validateForm}>
      {({ field, form }) => {
        const errors = getIn(form.errors, name);
        const touched = getIn(form.touched, name);
        const hasError = errors && touched;

        let errorsComponents = [];
        if (errors) {
          errorsComponents = errors.map(errorItem => (
            <li key={errorItem}>{errorItem}</li>
          ));
        }

        return (
          <FormElement
            label={label}
            isInvalid={hasError}
            isRequired={required}
            errorMessage={errorsComponents}
          >
            <Box id={name} maxWidth={theme.maxFieldsWidth}>
              <CKEditor
                data={field.value}
                onBeforeLoad={CKEDITOR => (CKEDITOR.disableAutoInline = true)}
                onChange={evt => {
                  field.onChange(name)(evt.editor.getData());
                }}
                onBlur={() => {
                  field.onBlur(name)(true);
                }}
                config={{
                  format_tags: 'p;h2;h3',
                  toolbar: [
                    [
                      'Format',
                      'Bold',
                      'Italic',
                      'NumberedList',
                      'BulletedList',
                      'Link',
                      'Unlink',
                      'SpecialChar',
                      'PasteFromWord',
                    ],
                  ],
                  versionCheck: false,
                  contentsCss: `data:text/css;charset=utf-8,${encodeURIComponent(customCSS)}`
                }}
              />
            </Box>
          </FormElement>
        );
      }}
    </Field>
  );
}
